import { Loader2, CheckCircle2, AlertTriangle, User } from 'lucide-react';
import { AgentOutput } from '../../lib/agents/types';

interface AgentStatusCardProps {
    name: string;
    role: string;
    status: 'idle' | 'analyzing' | 'complete' | 'error';
    output?: AgentOutput;
}

export function AgentStatusCard({ name, role, status, output }: AgentStatusCardProps) {
    return (
        <div className={`p-6 rounded-2xl border transition-all duration-300 ${status === 'analyzing' ? 'bg-blue-500/5 border-blue-500/20 animate-pulse' :
            status === 'complete' ? 'bg-green-500/5 border-green-500/20' :
                status === 'error' ? 'bg-red-500/5 border-red-500/20' :
                    'bg-white/5 border-white/10'
            }`}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${status === 'complete' ? 'bg-green-500/10' :
                        status === 'error' ? 'bg-red-500/10' :
                            'bg-blue-500/10'
                        }`}>
                        <User className={`w-4 h-4 ${status === 'complete' ? 'text-green-400' :
                            status === 'error' ? 'text-red-400' :
                                'text-blue-400'
                            }`} />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-white leading-tight">{name}</h4>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{role}</p>
                    </div>
                </div>

                {status === 'analyzing' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                {status === 'complete' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                {status === 'error' && <AlertTriangle className="w-4 h-4 text-red-500" />}
            </div>

            {status === 'complete' && output && (
                <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-1000 ${output.confidence > 0.8 ? 'bg-green-500' :
                                    output.confidence > 0.5 ? 'bg-yellow-500' :
                                        'bg-red-500'
                                    }`}
                                style={{ width: `${output.confidence * 100}%` }}
                            />
                        </div>
                        <span className="text-[10px] font-mono text-gray-400">{Math.round(output.confidence * 100)}%</span>
                    </div>

                    <p className="text-xs text-gray-400 leading-relaxed italic">
                        "{output.reasoning}"
                    </p>

                    {output.findings.length > 0 && (
                        <div className="pt-2 border-t border-white/5">
                            <div className="flex flex-wrap gap-1">
                                {output.findings.map((finding, i) => (
                                    <span key={i} className="text-[9px] px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded border border-red-500/10">
                                        {finding}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {status === 'idle' && (
                <p className="text-xs text-gray-600 italic">Waiting for signal...</p>
            )}

            {status === 'analyzing' && (
                <p className="text-xs text-blue-400/60 animate-pulse">Running reconnaissance...</p>
            )}
        </div>
    );
}
